// CSV シリアライズ/パース ユーティリティ。
// RFC 4180 準拠。BOM 付き UTF-8 で出力し、Excel 等で文字化けせず開けるようにする。
//
// - csvSerialize: 行配列 + カラム名配列 から CSV 文字列を生成する
// - csvParseStrict: CSV 文字列を行レコードへ復元する。書式の乱れを補正せず、
//                   利用者が直せる案内文と位置情報を付けたエラーの一覧として返す。
//                   マスターCSV取り込みのように、1件でも違反があれば取り込まない用途で使う
//
// 先頭行について:
//   1行目には見出し行の前に置く「先頭行」が入る。呼び出し側がファイルの種別などを書き込むための行で、
//   見出し行と列数を揃える必要はなく、列数の突き合わせの対象にもしない。中身はそのまま呼び出し側へ返す。
//   このため見出し行は2行目、最初のデータ行は3行目になり、利用者に見せる行番号もその数え方に合わせる。
//
// 制約:
//   - 外部依存なし(標準 JS の文字列操作のみ)
//   - クォート内のカンマ・改行・ダブルクォートをサポート
//   - 行末は CRLF / LF 双方を受理する

import { embeddedValueTextOf } from "./embeddedValueText";

// UTF-8 BOM。Excel が UTF-8 として認識するためのマーカー。
const BOM = "﻿";

// 1 セル分の値を CSV 用に文字列化する。
// 値がカンマ・改行・ダブルクォートを含む場合は "..." で囲み、内部の " を "" にエスケープする。
function escapeCell(value: unknown): string {
  if (value === null || value === undefined) {
    return "";
  }
  const s = String(value);
  const needsQuote = s.indexOf(",") >= 0 || s.indexOf("\"") >= 0 || s.indexOf("\n") >= 0 || s.indexOf("\r") >= 0;
  if (needsQuote) {
    const escaped = s.split("\"").join("\"\"");
    return "\"" + escaped + "\"";
  }
  return s;
}

// 行配列 + カラム名配列 から CSV 文字列を作る。
// 行は任意のオブジェクト配列で、columns に列挙されたキーの値だけを順に書き出す。
// leadRow は1行目に置く先頭行のセル。見出しと列数が違っていてもよく、csvParseStrict も
// 列数の突き合わせから外して読み戻す。空にすると空行になり読み戻せないため、必ず1つ以上のセルを渡す。
// 先頭行・ヘッダ行・データ行を CRLF 区切りで連結し、先頭に UTF-8 BOM を付与する。
export function csvSerialize(rows: Array<Record<string, unknown>>, columns: string[], leadRow: readonly string[]): string {
  const lead = leadRow.map(escapeCell).join(",");
  const header = columns.map(escapeCell).join(",");
  const body = rows.map((row) => {
    const cells = columns.map((col) => escapeCell(row[col]));
    return cells.join(",");
  });
  const allLines = [lead, header].concat(body);
  return BOM + allLines.join("\r\n");
}

// テキストの先頭にある UTF-8 BOM を取り除く。
function stripBom(text: string): string {
  if (text.length > 0 && text.charCodeAt(0) === 0xFEFF) {
    return text.slice(1);
  }
  return text;
}

// 厳格パースが見つけた不備1件。
// rowNumber は利用者へ見せるための行番号。空行も1行として数えた記録の並び順であり、
//   引用符で囲まれたセルが改行を含んでも増えないため、表計算ソフトで開いたときの行と一致する。
//   画面に出す行番号は必ずこちらを使う。
// textLine は元テキストの物理的な行番号。引用符内の改行も数えるため rowNumber とずれることがある。
//   テキストエディタで位置を突き止めたいときの手がかりとして残しており、利用者向けの表示には使わない。
// column は何列目のセルの話かを1始まりで示す。行全体の問題で列を特定できない場合は null。
// message はそのまま画面に出せる日本語の案内文。
export type CsvParseError = { rowNumber: number; textLine: number; column: number | null; message: string };

// 解析済みの1データ行。rowNumber は表計算ソフトで開いたときの行番号に一致する。
// 空行も1行として数え、引用符内の改行では増えない、テキスト上の記録の並び順そのもの。
// 先頭行が1行目・ヘッダ行が2行目のため、最初のデータ行は3行目になる。
export type CsvParsedRow = { rowNumber: number; values: Record<string, string> };

// 1行目に置かれた先頭行。cells はそこに書かれたセルをそのまま並べたもの。
// rowNumber は表計算ソフトで開いたときの行番号で、先頭行より前に空行があるとその分だけ大きくなる。
export type CsvLeadRow = { rowNumber: number; cells: string[] };

// 厳格パースの結果。成功なら中身、失敗なら見つかった不備の一覧を返す。
// leadRow は1行目の先頭行。中身の妥当性は呼び出し側が判断するため、ここでは読み取った内容をそのまま渡す。
//   空のテキストのように行が1つも無い場合は null になる。
// headerRowNumber はヘッダ行が表計算ソフトの何行目にあたるかを示す。先頭行の次の行のため通常は2行目で、
//   空行が挟まるとその分だけ大きくなる。先頭行しか無いテキストでは null になる。
export type CsvParseResult =
  | { ok: true; leadRow: CsvLeadRow | null; headers: string[]; headerRowNumber: number | null; rows: CsvParsedRow[] }
  | { ok: false; errors: CsvParseError[] };

// 分解した1行分。
// cells は行に含まれるセルの値。
// rowNumber は空行も1件として数えた記録の並び順で、表計算ソフトで開いたときの行番号に一致する。
// textLine はテキスト上で何行目から始まったかで、引用符内の改行も数えた物理的な位置。
// blank は「区切りも引用符も無い、完全に空の行」であることを表す。
type CsvRecord = { cells: string[]; rowNumber: number; textLine: number; blank: boolean };

// テキストを1行ずつのセル配列へ分解した結果と、その過程で見つかった書式の不備。
type CsvTokenizeResult = { records: CsvRecord[]; errors: CsvParseError[] };

const UNCLOSED_QUOTE_MESSAGE =
  "二重引用符が閉じられていません。この位置から始まるセルの末尾に二重引用符を追加してください。値の中で二重引用符を使う場合は2つ重ねて書いてください。";
const TEXT_AFTER_QUOTE_MESSAGE =
  "二重引用符で閉じたセルの後ろに、区切りのカンマでも改行でもない文字があります。セル全体を二重引用符で囲むか、値の中の二重引用符を2つ重ねて書いてください。";
const BARE_QUOTE_MESSAGE =
  "二重引用符で囲まれていないセルの中に二重引用符があります。セル全体を二重引用符で囲み、値の中の二重引用符は2つ重ねて書いてください。";

// CSV テキストを1文字ずつ読み進めて行とセルに分解する。
// 引用符の閉じ忘れや、引用符で閉じた後に余計な文字が続く書き方をその場で不備として拾う。
// 文字を読み進める性質上、読み取り位置と組み立て中のセルだけは書き換えながら進める。
function tokenizeStrict(text: string): CsvTokenizeResult {
  const records: CsvRecord[] = [];
  const errors: CsvParseError[] = [];
  let index = 0;
  let line = 1;
  let recordLine = 1;
  let cells: string[] = [];
  let cell = "";
  let cellQuoted = false;
  let insideQuotes = false;
  let quoteClosed = false;
  let quoteOpenLine = 1;
  let quoteOpenRowNumber = 1;
  let bareQuoteReported = false;
  while (index < text.length) {
    const ch = text.charAt(index);
    const columnNumber = cells.length + 1;
    // 組み立て中の行が、空行も数えた並び順で何行目にあたるか。引用符内の改行では増えない。
    const currentRowNumber = records.length + 1;
    if (insideQuotes) {
      const isEscapedQuote = ch === "\"" && text.charAt(index + 1) === "\"";
      const isCarriageReturnAndNewline = ch === "\r" && text.charAt(index + 1) === "\n";
      const isLineBreak = ch === "\r" || ch === "\n";
      if (isEscapedQuote) {
        cell += "\"";
        index += 2;
      } else if (ch === "\"") {
        insideQuotes = false;
        quoteClosed = true;
        index += 1;
      } else if (isCarriageReturnAndNewline) {
        // 復帰と改行の2文字で1つの改行。値としては元の並びのまま残す。
        cell += "\r\n";
        line += 1;
        index += 2;
      } else if (isLineBreak) {
        // 復帰だけ・改行だけの改行も1行として数える。数え落とすと以降の不備の位置がずれる。
        cell += ch;
        line += 1;
        index += 1;
      } else {
        cell += ch;
        index += 1;
      }
    } else if (ch === "\r" || ch === "\n") {
      cells.push(cell);
      const isOnlyEmptyCell = cells.length === 1 && cell === "" && !cellQuoted;
      records.push({ cells: cells, rowNumber: currentRowNumber, textLine: recordLine, blank: isOnlyEmptyCell });
      const isCrLf = ch === "\r" && text.charAt(index + 1) === "\n";
      if (isCrLf) {
        index += 2;
      } else {
        index += 1;
      }
      line += 1;
      recordLine = line;
      cells = [];
      cell = "";
      cellQuoted = false;
      quoteClosed = false;
      bareQuoteReported = false;
    } else if (ch === ",") {
      cells.push(cell);
      cell = "";
      cellQuoted = false;
      quoteClosed = false;
      bareQuoteReported = false;
      index += 1;
    } else if (quoteClosed) {
      // 同じセルで何度も同じ指摘を出さないよう、1文字目だけ拾って残りは通常の文字として読み進める。
      errors.push({ rowNumber: currentRowNumber, textLine: line, column: columnNumber, message: TEXT_AFTER_QUOTE_MESSAGE });
      quoteClosed = false;
      cell += ch;
      index += 1;
    } else if (ch === "\"") {
      const startsQuotedCell = cell === "" && !cellQuoted;
      if (startsQuotedCell) {
        cellQuoted = true;
        insideQuotes = true;
        quoteOpenLine = line;
        quoteOpenRowNumber = currentRowNumber;
        index += 1;
      } else {
        if (!bareQuoteReported) {
          errors.push({ rowNumber: currentRowNumber, textLine: line, column: columnNumber, message: BARE_QUOTE_MESSAGE });
          bareQuoteReported = true;
        }
        cell += ch;
        index += 1;
      }
    } else {
      cell += ch;
      index += 1;
    }
  }
  if (insideQuotes) {
    // 閉じ忘れた引用符から後ろは、どこまでが1つのセルなのか決められない。
    // 続きを読んでも意味のある指摘にならないため、ここまでで打ち切る。
    errors.push({ rowNumber: quoteOpenRowNumber, textLine: quoteOpenLine, column: cells.length + 1, message: UNCLOSED_QUOTE_MESSAGE });
    return { records: records, errors: errors };
  }
  const hasTrailingRecord = cell.length > 0 || cells.length > 0 || cellQuoted;
  if (hasTrailingRecord) {
    cells.push(cell);
    const isOnlyEmptyCell = cells.length === 1 && cell === "" && !cellQuoted;
    records.push({ cells: cells, rowNumber: records.length + 1, textLine: recordLine, blank: isOnlyEmptyCell });
  }
  return { records: records, errors: errors };
}

// ヘッダ行の列名を点検する。空の列名と、既に出てきた列名の重複を不備として集める。
function collectHeaderErrors(header: CsvRecord): CsvParseError[] {
  const errors: CsvParseError[] = [];
  const seenNames = new Set<string>();
  for (let i = 0; i < header.cells.length; i++) {
    const name = header.cells[i] ?? "";
    const columnNumber = i + 1;
    if (name === "") {
      const message = columnNumber + "列目のヘッダに列名がありません。列名を入力してください。";
      errors.push({ rowNumber: header.rowNumber, textLine: header.textLine, column: columnNumber, message: message });
    }
    const isDuplicated = name !== "" && seenNames.has(name);
    if (isDuplicated) {
      const message = columnNumber + "列目のヘッダの列名「" + embeddedValueTextOf(name) + "」が既に使われています。列名は重複しないようにしてください。";
      errors.push({ rowNumber: header.rowNumber, textLine: header.textLine, column: columnNumber, message: message });
    }
    seenNames.add(name);
  }
  return errors;
}

// データ行のセル数がヘッダと揃っているかを点検する。不足と余剰の双方を不備として集める。
function collectColumnCountErrors(headerCount: number, bodyRecords: CsvRecord[]): CsvParseError[] {
  const errors: CsvParseError[] = [];
  for (const record of bodyRecords) {
    const cellCount = record.cells.length;
    if (cellCount < headerCount) {
      // どの列が抜けたのかはテキストからは判断できないため、列位置は付けない。
      const message = "列数が足りません。ヘッダは" + headerCount + "列ですが、この行は" + cellCount + "列です。区切りのカンマを補ってください。";
      errors.push({ rowNumber: record.rowNumber, textLine: record.textLine, column: null, message: message });
    }
    if (cellCount > headerCount) {
      const message = "列数が多すぎます。ヘッダは" + headerCount + "列ですが、この行は" + cellCount + "列です。余分なセルを削るか、値に含まれるカンマを二重引用符で囲んでください。";
      errors.push({ rowNumber: record.rowNumber, textLine: record.textLine, column: headerCount + 1, message: message });
    }
  }
  return errors;
}

// ヘッダの列名をキーにして、1行分のセルをレコードへ組み替える。
function toRowRecord(headers: string[], cells: string[]): Record<string, string> {
  const row: Record<string, string> = {};
  for (let i = 0; i < headers.length; i++) {
    const key = headers[i];
    const value = cells[i];
    const isFilled = key !== undefined && value !== undefined;
    if (isFilled) {
      row[key] = value;
    }
  }
  return row;
}

// CSV テキストを厳格に解釈し、成功なら先頭行・列名・行レコード、失敗なら不備の一覧を返す。
// 書式・ヘッダ・列数の3段階で点検し、先の段階で不備が見つかった時点で打ち切る。
// 後段の指摘は前段の不備の言い換えになりやすく、同じ原因を何度も見せないため。
//
// 最初の行は先頭行として取り分け、その次の行をヘッダ、以降をデータ行として扱う。
// 先頭行は列数の突き合わせに加えず、ヘッダの列名としても扱わない。
// テキストが空、または完全に空の行しか無い場合は、先頭行も列も行も無い成功として扱う。
export function csvParseStrict(text: string): CsvParseResult {
  const tokenized = tokenizeStrict(stripBom(text));
  if (tokenized.errors.length > 0) {
    return { ok: false, errors: tokenized.errors };
  }
  const filledRecords = tokenized.records.filter((record) => !record.blank);
  const leadRecord = filledRecords[0];
  if (leadRecord === undefined) {
    return { ok: true, leadRow: null, headers: [], headerRowNumber: null, rows: [] };
  }
  const leadRow: CsvLeadRow = { rowNumber: leadRecord.rowNumber, cells: leadRecord.cells };
  const header = filledRecords[1];
  if (header === undefined) {
    return { ok: true, leadRow: leadRow, headers: [], headerRowNumber: null, rows: [] };
  }
  const headerErrors = collectHeaderErrors(header);
  if (headerErrors.length > 0) {
    return { ok: false, errors: headerErrors };
  }
  const bodyRecords = filledRecords.slice(2);
  const countErrors = collectColumnCountErrors(header.cells.length, bodyRecords);
  if (countErrors.length > 0) {
    return { ok: false, errors: countErrors };
  }
  const rows = bodyRecords.map((record) => {
    return { rowNumber: record.rowNumber, values: toRowRecord(header.cells, record.cells) };
  });
  return { ok: true, leadRow: leadRow, headers: header.cells, headerRowNumber: header.rowNumber, rows: rows };
}
